import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, User, Sparkles, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Strip markdown for TTS — removes **, *, ###, -, etc. so TTS reads clean text
const stripMarkdown = (text) => {
  return text
    .replace(/#{1,6}\s?/g, '')        // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')  // bold
    .replace(/\*(.+?)\*/g, '$1')      // italic
    .replace(/`(.+?)`/g, '$1')        // inline code
    .replace(/^\s*[-*+]\s+/gm, '')    // bullet points
    .replace(/^\s*\d+\.\s+/gm, '')    // numbered lists
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // links
    .replace(/\n{2,}/g, '. ')         // double newlines to pause
    .replace(/\n/g, ' ')              // single newlines
    .replace(/\s{2,}/g, ' ')          // collapse spaces
    .trim();
};

// Athena Avatar Component
const AthenaAvatar = () => (
  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0096D6] to-[#0077a8] flex items-center justify-center flex-shrink-0 shadow-lg">
    <Sparkles className="w-4 h-4 text-white" />
  </div>
);

// Speaker Button Component
const SpeakButton = ({ text, isPlaying, onPlay, onStop, isLoading }) => {
  return (
    <button
      onClick={isPlaying ? onStop : onPlay}
      disabled={isLoading}
      data-testid="speak-button"
      style={{
        background: 'none',
        border: 'none',
        cursor: isLoading ? 'wait' : 'pointer',
        padding: '4px 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        opacity: isLoading ? 0.7 : 1,
        color: isPlaying ? '#0096D6' : '#94a3b8',
        transition: 'color 0.2s'
      }}
      title={isLoading ? "Preparing Athena's voice..." : isPlaying ? "Stop speaking" : "Listen to Athena"}
    >
      {isLoading ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span className="voice-wave" style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            <span className="wave-bar" style={{ animationDelay: '0ms' }} />
            <span className="wave-bar" style={{ animationDelay: '150ms' }} />
            <span className="wave-bar" style={{ animationDelay: '300ms' }} />
          </span>
          <span style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'Manrope, sans-serif' }}>Preparing...</span>
        </span>
      ) : isPlaying ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <VolumeX style={{ width: '16px', height: '16px' }} />
        </span>
      ) : (
        <Volume2 style={{ width: '16px', height: '16px' }} />
      )}
    </button>
  );
};

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => uuidv4());
  const [showContactPrompt, setShowContactPrompt] = useState(false);
  const [leadInfo, setLeadInfo] = useState({ name: '', email: '', company: '' });
  const [leadSaved, setLeadSaved] = useState(false);
  const [playingMessageIndex, setPlayingMessageIndex] = useState(null);
  const [loadingAudioIndex, setLoadingAudioIndex] = useState(null);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const audioCacheRef = useRef({});  // Pre-fetched audio cache keyed by message index

  // Initial greeting when chat opens
  useEffect(() => {
    if (messages.length === 0 && isOpen) {
      const welcomeMessage = "Hello! I'm Athena, your dedicated assistant at Real Tech Services. I'm here to help with any questions about our wholesale electronics range, from audio equipment to smartphones and computing devices. How may I assist you today?";
      setMessages([{
        role: 'assistant',
        content: welcomeMessage
      }]);
    }
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Show contact prompt after several messages
  useEffect(() => {
    if (messages.length >= 6 && !showContactPrompt && !leadSaved) {
      setShowContactPrompt(true);
    }
  }, [messages.length, showContactPrompt, leadSaved]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Revoke all cached URLs
      Object.values(audioCacheRef.current).forEach(url => URL.revokeObjectURL(url));
      audioCacheRef.current = {};
    };
  }, []);

  // Pre-fetch TTS audio in the background for a message
  const prefetchAudio = async (text, messageIndex) => {
    try {
      const cleanText = stripMarkdown(text);
      const response = await fetch(`${BACKEND_URL}/api/chat/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: cleanText, voice: 'fable' })
      });
      if (response.ok) {
        const blob = await response.blob();
        audioCacheRef.current[messageIndex] = URL.createObjectURL(blob);
      }
    } catch (e) {
      // Silent fail for prefetch
    }
  };

  const speakText = async (text, messageIndex) => {
    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setPlayingMessageIndex(null);
      
      // Check if audio is already pre-fetched
      const cachedUrl = audioCacheRef.current[messageIndex];
      if (cachedUrl) {
        const audio = new Audio(cachedUrl);
        audioRef.current = audio;
        audio.onended = () => {
          setPlayingMessageIndex(null);
          audioRef.current = null;
        };
        audio.onerror = () => {
          setPlayingMessageIndex(null);
          audioRef.current = null;
        };
        setPlayingMessageIndex(messageIndex);
        await audio.play();
        return;
      }
      
      // Not cached — fetch now with loading indicator
      setLoadingAudioIndex(messageIndex);
      
      const cleanText = stripMarkdown(text);
      
      const response = await fetch(`${BACKEND_URL}/api/chat/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: cleanText, voice: 'fable' })
      });

      if (!response.ok) throw new Error(`TTS failed: ${response.status}`);

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = new Audio();
      audioRef.current = audio;
      
      audio.oncanplay = () => {
        if (audioRef.current === audio) {
          setLoadingAudioIndex(null);
          setPlayingMessageIndex(messageIndex);
          audio.play().catch(err => {
            console.error('Play failed:', err);
            setPlayingMessageIndex(null);
            setLoadingAudioIndex(null);
          });
        }
      };
      
      audio.onended = () => {
        setPlayingMessageIndex(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      
      audio.onerror = (e) => {
        console.error('Audio error:', e);
        setPlayingMessageIndex(null);
        setLoadingAudioIndex(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      
      audio.src = audioUrl;
      audio.load();
      
    } catch (error) {
      console.error('TTS error:', error);
      setLoadingAudioIndex(null);
      setPlayingMessageIndex(null);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingMessageIndex(null);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      
      const assistantMessage = response.data.response;
      const newMessageIndex = messages.length + 1; // +1 for the user message we just added
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: assistantMessage 
      }]);

      // Pre-fetch TTS audio in background so it's ready when user clicks speak
      prefetchAudio(assistantMessage, newMessageIndex);

      // Auto-speak if enabled
      if (autoSpeak) {
        setTimeout(() => speakText(assistantMessage, newMessageIndex), 500);
      }

      if (response.data.suggested_action === 'contact_form' && !leadSaved) {
        setShowContactPrompt(true);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologise, but I'm experiencing a brief connection issue. Please try again, or feel free to reach us directly at info@realtechservices.co.uk or call +44(0) 203 059 4550."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveLead = async () => {
    if (!leadInfo.email) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/chat/save-lead`, {
        session_id: sessionId,
        name: leadInfo.name,
        email: leadInfo.email,
        company: leadInfo.company
      });
      setLeadSaved(true);
      setShowContactPrompt(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Thank you${leadInfo.name ? `, ${leadInfo.name}` : ''}! I've noted your details. Our team will be in touch shortly with personalised information. Is there anything else I can help you with in the meantime?`
      }]);
    } catch (error) {
      console.error('Error saving lead:', error);
    }
  };

  const chatContent = (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '96px',
          right: '24px',
          zIndex: 99999,
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0096D6 0%, #0077a8 100%)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 30px rgba(0, 150, 214, 0.4)',
          transition: 'transform 0.2s, box-shadow 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 150, 214, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 150, 214, 0.4)';
        }}
        data-testid="chat-toggle-button"
      >
        {isOpen ? (
          <X style={{ width: '24px', height: '24px' }} />
        ) : (
          <div style={{ position: 'relative' }}>
            <MessageCircle style={{ width: '28px', height: '28px' }} />
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '12px',
              height: '12px',
              background: '#4ade80',
              borderRadius: '50%',
              border: '2px solid white'
            }} />
          </div>
        )}
      </button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              bottom: '176px',
              right: '24px',
              zIndex: 99999,
              width: '420px',
              maxWidth: 'calc(100vw - 48px)',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden',
              border: '1px solid #e2e8f0'
            }}
            data-testid="chat-window"
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(90deg, #0096D6 0%, #0077a8 100%)',
              padding: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ position: 'relative' }}>
                  <AthenaAvatar />
                  <span style={{
                    position: 'absolute',
                    bottom: '-2px',
                    right: '-2px',
                    width: '12px',
                    height: '12px',
                    background: '#4ade80',
                    borderRadius: '50%',
                    border: '2px solid #0096D6'
                  }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 600,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: 0
                  }}>
                    Athena
                    <Sparkles style={{ width: '16px', height: '16px', color: '#a5f3fc' }} />
                  </h3>
                  <p style={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '14px',
                    fontFamily: 'Manrope, sans-serif',
                    margin: 0
                  }}>AI Sales Assistant • Online</p>
                </div>
                {/* Auto-speak toggle */}
                <button
                  onClick={() => setAutoSpeak(!autoSpeak)}
                  style={{
                    background: autoSpeak ? 'rgba(255,255,255,0.2)' : 'transparent',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: 'white',
                    fontSize: '11px',
                    fontFamily: 'Manrope, sans-serif'
                  }}
                  title={autoSpeak ? "Disable auto-speak" : "Enable auto-speak"}
                >
                  <Volume2 style={{ width: '14px', height: '14px' }} />
                  <span>{autoSpeak ? 'ON' : 'OFF'}</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div style={{
              height: '380px',
              overflowY: 'auto',
              padding: '16px',
              background: 'linear-gradient(180deg, #f8fafc 0%, white 100%)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {messages.map((message, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                    }}
                  >
                    {message.role === 'assistant' && <AthenaAvatar />}
                    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '80%' }}>
                      <div
                        style={{
                          padding: '14px',
                          fontFamily: 'Manrope, sans-serif',
                          fontSize: '14px',
                          lineHeight: 1.6,
                          borderRadius: '16px',
                          ...(message.role === 'user' ? {
                            background: '#0096D6',
                            color: 'white',
                            borderBottomRightRadius: '4px',
                            boxShadow: '0 4px 6px -1px rgba(0, 150, 214, 0.3)'
                          } : {
                            background: 'white',
                            color: '#334155',
                            borderBottomLeftRadius: '4px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            border: '1px solid #f1f5f9'
                          })
                        }}
                        data-testid={`chat-message-${message.role}`}
                      >
                        {message.role === 'assistant' ? (
                          <div className="athena-msg">
                            <ReactMarkdown
                              components={{
                                p: ({children}) => <p style={{ margin: '0 0 12px 0', lineHeight: 1.75, letterSpacing: '0.01em' }}>{children}</p>,
                                strong: ({children}) => <strong style={{ fontWeight: 700, color: '#0077a8' }}>{children}</strong>,
                                em: ({children}) => <em style={{ color: '#475569' }}>{children}</em>,
                                h3: ({children}) => <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px', margin: '14px 0 8px 0', paddingBottom: '6px', borderBottom: '2px solid #0096D6' }}>{children}</div>,
                                h4: ({children}) => <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px', margin: '10px 0 4px 0' }}>{children}</div>,
                                ul: ({children}) => <div style={{ margin: '8px 0 12px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>{children}</div>,
                                ol: ({children}) => <ol style={{ margin: '8px 0 12px 0', paddingLeft: '18px' }}>{children}</ol>,
                                li: ({children}) => (
                                  <div style={{
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%)',
                                    border: '1px solid #e0f2fe',
                                    fontSize: '13px',
                                    lineHeight: 1.6,
                                  }}>
                                    {children}
                                  </div>
                                ),
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          </div>
                        ) : message.content}
                      </div>
                      {/* Speaker button for assistant messages */}
                      {message.role === 'assistant' && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          marginTop: '4px',
                          marginLeft: '8px'
                        }}>
                          <SpeakButton
                            text={message.content}
                            isPlaying={playingMessageIndex === index}
                            isLoading={loadingAudioIndex === index}
                            onPlay={() => speakText(message.content, index)}
                            onStop={stopSpeaking}
                          />
                          {playingMessageIndex === index && (
                            <span style={{
                              fontSize: '11px',
                              color: '#0096D6',
                              fontFamily: 'Manrope, sans-serif'
                            }}>
                              Speaking...
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: '#e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <User style={{ width: '16px', height: '16px', color: '#64748b' }} />
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Loading indicator */}
                {isLoading && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <AthenaAvatar />
                    <div style={{
                      background: 'white',
                      padding: '14px',
                      borderRadius: '16px',
                      borderBottomLeftRadius: '4px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      border: '1px solid #f1f5f9'
                    }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <span className="bounce-dot" style={{ animationDelay: '0ms' }} />
                        <span className="bounce-dot" style={{ animationDelay: '150ms' }} />
                        <span className="bounce-dot" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Lead Collection Form */}
                {showContactPrompt && !leadSaved && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(0,150,214,0.05) 0%, rgba(0,150,214,0.1) 100%)',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid rgba(0,150,214,0.2)'
                  }}>
                    <p style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '14px',
                      color: '#334155',
                      marginBottom: '12px'
                    }}>
                      I'd love to send you more detailed information. May I have your contact details?
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={leadInfo.name}
                        onChange={(e) => setLeadInfo(prev => ({ ...prev, name: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '14px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      <input
                        type="email"
                        placeholder="Business email *"
                        value={leadInfo.email}
                        onChange={(e) => setLeadInfo(prev => ({ ...prev, email: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '14px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Company name"
                        value={leadInfo.company}
                        onChange={(e) => setLeadInfo(prev => ({ ...prev, company: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '14px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
                        <button
                          onClick={saveLead}
                          disabled={!leadInfo.email}
                          style={{
                            flex: 1,
                            background: '#0096D6',
                            color: 'white',
                            fontSize: '14px',
                            padding: '8px',
                            borderRadius: '8px',
                            fontWeight: 500,
                            border: 'none',
                            cursor: leadInfo.email ? 'pointer' : 'not-allowed',
                            opacity: leadInfo.email ? 1 : 0.5
                          }}
                        >
                          Submit
                        </button>
                        <button
                          onClick={() => setShowContactPrompt(false)}
                          style={{
                            padding: '8px 16px',
                            color: '#64748b',
                            fontSize: '14px',
                            borderRadius: '8px',
                            fontWeight: 500,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer'
                          }}
                        >
                          Later
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Quick Actions */}
            {messages.length >= 2 && (
              <div style={{
                padding: '8px 16px',
                borderTop: '1px solid #f1f5f9',
                background: '#f8fafc',
                display: 'flex',
                gap: '8px'
              }}>
                <Link
                  to="/products"
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '12px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    color: '#64748b',
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                >
                  Browse Products
                </Link>
                <Link
                  to="/contact"
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '12px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: '#0096D6',
                    color: 'white',
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                >
                  Request Quote
                </Link>
              </div>
            )}

            {/* Input */}
            <form onSubmit={sendMessage} style={{
              padding: '16px',
              borderTop: '1px solid #e2e8f0',
              background: 'white'
            }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Athena anything..."
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '12px',
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    outline: 'none',
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '14px'
                  }}
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #0096D6 0%, #0077a8 100%)',
                    color: 'white',
                    border: 'none',
                    cursor: (isLoading || !input.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (isLoading || !input.trim()) ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 6px -1px rgba(0, 150, 214, 0.3)'
                  }}
                  data-testid="chat-send-button"
                >
                  <Send style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CSS for animations */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes waveAnim {
          0%, 100% { height: 4px; }
          50% { height: 14px; }
        }
        .bounce-dot {
          width: 8px;
          height: 8px;
          background: #0096D6;
          border-radius: 50%;
          animation: bounce 1s infinite;
        }
        .wave-bar {
          width: 3px;
          height: 4px;
          background: #0096D6;
          border-radius: 2px;
          animation: waveAnim 0.8s ease-in-out infinite;
        }
        .athena-msg p:last-child {
          margin-bottom: 0 !important;
        }
        .athena-msg ul:last-child,
        .athena-msg ol:last-child {
          margin-bottom: 0 !important;
        }
      `}</style>
    </>
  );

  // Use portal to render directly to body
  return createPortal(chatContent, document.body);
};
